import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Redirect a /flights/:sessionId style path to its V4 equivalent, preserving
// the param AND the query string (?admin_key=... must survive for backoffice SSR).
function RedirectFlightDetail() {
  const { sessionId } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/v4/flights/${sessionId}${search}`} replace />;
}

// Redirect /hangar and /hangar/:icao to /v4, preserving the period (?period=).
function RedirectHangar() {
  const { icao } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/v4/hangar${icao ? `/${icao}` : ''}${search}`} replace />;
}

// Redirect /settings/:section to /v4/settings/:section, preserving query string.
function RedirectSettingsSection() {
  const { section } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/v4/settings/${section}${search}`} replace />;
}
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Auth Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
// V4-styled public page (no login): reached from the password-reset e-mail link.
const ResetPasswordV4 = lazy(() => import('./v4/pages/auth/ResetPasswordStandalone'));
// Flight Story shared by its pilot (PL-FS-005): readable without an account.
const PublicFlightStory = lazy(() => import('./v4/pages/PublicFlightStory'));
import SuccessPage from './pages/SuccessPage';
import AuthCallbackPage from './pages/AuthCallbackPage';

// Error Pages
import NotFoundPage from './pages/errors/NotFoundPage';
import ServerErrorPage from './pages/errors/ServerErrorPage';
import ForbiddenPage from './pages/errors/ForbiddenPage';
import MaintenancePage from './pages/errors/MaintenancePage';

// Legacy dashboard pages decommissioned — kept only RankingPage / RankingProfilePage
// for the PUBLIC /worldranking routes (accessible without login)
const RankingPage = lazy(() => import('./pages/dashboard/RankingPage'));
const RankingProfilePage = lazy(() => import('./pages/dashboard/RankingProfilePage'));

// Dev pages — registered in development only. `import.meta.env.DEV` is inlined
// at build time, so in production this branch is dead code and the pages are
// not bundled at all (no chunk, no route).
const DEV_ROUTES = import.meta.env.DEV
  ? [
      {
        path: '/dev/gamification/basic',
        Component: lazy(() => import('./pages/BasicGamificationDemoPage')),
      },
      { path: '/dev/map-test', Component: lazy(() => import('./pages/test/MapTestPage')) },
    ]
  : [];

// V4 - Nouvelle version frontend
const V4App = lazy(() => import('./v4/App'));

// Legal Pages
import LegalPage from './pages/LegalPage';

// Loading Component
const PageLoader = () => {
  const { t } = useTranslation();
  return (
    <div className="loading-screen">
      <div className="loading-spinner">
        <div className="spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    </div>
  );
};

// Styles
import './styles/auth.css';
import './styles/error.css';
import './components/dashboard/DashboardLayout.css';

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public Routes - Auth */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordV4 />} />
              <Route path="/story/:token" element={<PublicFlightStory />} />
              <Route path="/success" element={<SuccessPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />

              {/* Legal Pages - Multilingual */}
              <Route path="/:lang/confidentialite" element={<LegalPage />} />
              <Route path="/:lang/conditions-utilisation" element={<LegalPage />} />
              <Route path="/:lang/cookies" element={<LegalPage />} />
              <Route path="/:lang/privacy-policy" element={<LegalPage />} />
              <Route path="/:lang/terms-of-service" element={<LegalPage />} />
              <Route path="/:lang/cookie-policy" element={<LegalPage />} />
              <Route path="/:lang/datenschutz" element={<LegalPage />} />
              <Route path="/:lang/nutzungsbedingungen" element={<LegalPage />} />
              <Route path="/:lang/privacidad" element={<LegalPage />} />
              <Route path="/:lang/terminos-servicio" element={<LegalPage />} />
              <Route path="/:lang/privacy" element={<LegalPage />} />
              <Route path="/:lang/termini-servizio" element={<LegalPage />} />

              {/* Error Pages */}
              <Route path="/403" element={<ForbiddenPage />} />
              <Route path="/500" element={<ServerErrorPage />} />
              <Route path="/maintenance" element={<MaintenancePage />} />

              {/* V4 - Nouvelle version frontend */}
              <Route path="/v4/*" element={<V4App />} />

              {/* Dev routes — development builds only */}
              {DEV_ROUTES.map(({ path, Component }) => (
                <Route key={path} path={path} element={<Component />} />
              ))}

              {/* Public World Ranking - No login required */}
              <Route path="/worldranking" element={<RankingPage />} />
              <Route path="/worldranking/pilot/:id" element={<RankingProfilePage />} />

              {/* Legacy UI fully decommissioned — every legacy route redirects to its V4 equivalent */}
              <Route path="/dashboard" element={<Navigate to="/v4" replace />} />
              <Route path="/flights" element={<Navigate to="/v4/flights" replace />} />
              <Route path="/flights/:sessionId" element={<RedirectFlightDetail />} />
              <Route path="/dashboard/flights/:sessionId" element={<RedirectFlightDetail />} />
              <Route path="/profile" element={<Navigate to="/v4/settings/profile" replace />} />
              <Route path="/settings" element={<Navigate to="/v4/settings/profile" replace />} />
              <Route path="/settings/:section" element={<RedirectSettingsSection />} />
              <Route path="/leaderboard" element={<Navigate to="/v4/leaderboard" replace />} />
              <Route
                path="/leaderboard/pilot/:id"
                element={<Navigate to="/v4/leaderboard" replace />}
              />
              <Route path="/progression" element={<Navigate to="/v4/progression" replace />} />
              <Route path="/badges" element={<Navigate to="/v4/badges" replace />} />
              <Route path="/achievements" element={<Navigate to="/v4/badges" replace />} />
              <Route path="/support" element={<Navigate to="/v4/support" replace />} />
              <Route path="/discovery" element={<Navigate to="/v4/discovery" replace />} />
              <Route path="/hangar" element={<RedirectHangar />} />
              <Route path="/hangar/:icao" element={<RedirectHangar />} />
              <Route path="/analysis/fuel" element={<Navigate to="/v4" replace />} />
              <Route path="/analysis/fuel-phases" element={<Navigate to="/v4" replace />} />
              <Route path="/analysis/fuel-leaderboard" element={<Navigate to="/v4" replace />} />
              <Route path="/analytics" element={<Navigate to="/v4" replace />} />
              <Route path="/compare" element={<Navigate to="/v4" replace />} />
              <Route path="/organization" element={<Navigate to="/v4" replace />} />

              {/* Redirect root to V4 (new design) */}
              <Route path="/" element={<Navigate to="/v4" replace />} />

              {/* 404 - Must be last */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
